import React, { useEffect, useMemo, useState } from "react";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { AiFillShopping, AiOutlineCheckCircle, AiOutlineSearch, AiOutlineStop } from "react-icons/ai";
import Sidebaradmin from "../Sidebar";
import Navbaradmin from "../navbar";

const emptyForm = {
  name: "",
  description: "",
  price: "",
  category: "",
  stock: "",
  images: "",
  isActive: true,
  subscriptionPlan: "",
  months: 1,
  totalPrice: 0,
  planID: "",
  additionalDetails: "",
  promotion: "",
  cancelledMRP: "",
};

const inputClass =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";
const textAreaClass =
  "min-h-24 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "lg:col-span-2" : ""}`}>
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const formatMoney = (value) => {
  const number = Number(value || 0);
  return `$${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const statusClass = (isActive) =>
  isActive ? "bg-emerald-50 text-emerald-700 ring-emerald-100" : "bg-rose-50 text-rose-700 ring-rose-100";

const ProductAdmin = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/products");
      const data = await response.json();
      if (response.ok) {
        setProducts(Array.isArray(data) ? data : []);
      } else {
        setError("Failed to fetch products");
      }
    } catch (err) {
      setError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const calculateTotalPrice = (price, plan, months) => {
    const pricePerMonth = parseFloat(price) || 0;
    return pricePerMonth * (parseInt(months, 10) || 1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => {
      const updatedForm = { ...prev, [name]: value };
      if (name === "subscriptionPlan" || name === "months" || name === "price") {
        updatedForm.totalPrice = calculateTotalPrice(updatedForm.price, updatedForm.subscriptionPlan, updatedForm.months);
      }
      return updatedForm;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const method = editId ? "PUT" : "POST";
      const url = editId ? `/api/admin/products/${editId}` : "/api/admin/products";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setForm(emptyForm);
        setEditId(null);
        setError("");
        fetchProducts();
      } else {
        setError("Failed to save product");
      }
    } catch (err) {
      setError("Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditId(product._id);
    setForm({
      name: product.name || "",
      description: product.description || "",
      price: product.price || "",
      category: product.category || "",
      stock: product.stock || "",
      images: Array.isArray(product.images) ? product.images.join(",") : product.images || "",
      isActive: product.isActive ?? true,
      subscriptionPlan: product.subscriptionPlan || "",
      months: product.months || 1,
      totalPrice: product.totalPrice || 0,
      planID: product.planID || "",
      additionalDetails: product.additionalDetails || "",
      promotion: product.promotion || "",
      cancelledMRP: product.cancelledMRP || "",
    });
  };

  const handleDelete = async (id) => {
    const shouldDelete = window.confirm("Are you sure you want to delete this product?");
    if (!shouldDelete) return;

    try {
      const response = await fetch(`/api/admin/products/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        fetchProducts();
      } else {
        setError("Failed to delete product");
      }
    } catch (err) {
      setError("Failed to delete product");
    }
  };

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const haystack = `${product?.name || ""} ${product?.category || ""} ${product?.subscriptionPlan || ""}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      }),
    [products, search]
  );

  const activeCount = products.filter((product) => product.isActive).length;
  const inactiveCount = products.length - activeCount;
  const totalValue = products.reduce((sum, product) => sum + Number(product.totalPrice || product.price || 0), 0);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebaradmin />

        <div className="flex min-w-0 flex-1 flex-col">
          <Navbaradmin />

          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <section className="mb-6 flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Product management</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Admin Products
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                  Create plans, manage subscriptions, pricing, stock, promotions, and product visibility.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:min-w-[420px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase text-slate-500">Products</p>
                  <p className="mt-1 text-2xl font-bold text-slate-950">{products.length}</p>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-xs font-semibold uppercase text-emerald-700">Active</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-800">{activeCount}</p>
                </div>
                <div className="rounded-lg border border-sky-100 bg-sky-50 p-3">
                  <p className="text-xs font-semibold uppercase text-sky-700">Value</p>
                  <p className="mt-1 text-2xl font-bold text-sky-800">{formatMoney(totalValue)}</p>
                </div>
              </div>
            </section>

            {error && (
              <div className="mb-6 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {error}
              </div>
            )}

            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{editId ? "Edit Product" : "Add New Product"}</h2>
                  <p className="text-sm text-slate-500">Fill product details, subscription plan settings, and promotional metadata.</p>
                </div>
                {editId && (
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    onClick={() => {
                      setEditId(null);
                      setForm(emptyForm);
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field label="Product Name">
                    <input type="text" name="name" value={form.name} onChange={handleChange} className={inputClass} required />
                  </Field>

                  <Field label="Category">
                    <input type="text" name="category" value={form.category} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="Product Description" wide>
                    <textarea name="description" value={form.description} onChange={handleChange} className={textAreaClass} />
                  </Field>

                  <Field label="Price">
                    <input type="number" name="price" value={form.price} onChange={handleChange} className={inputClass} required />
                  </Field>

                  <Field label="Cancelled MRP">
                    <input type="number" name="cancelledMRP" value={form.cancelledMRP} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="Stock Quantity">
                    <input type="number" name="stock" value={form.stock} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="Image URLs">
                    <input
                      type="text"
                      name="images"
                      placeholder="Comma-separated URLs"
                      value={form.images}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Subscription Plan">
                    <select name="subscriptionPlan" value={form.subscriptionPlan} onChange={handleChange} className={inputClass}>
                      <option value="">Select Subscription Plan</option>
                      <option value="StarterPlan">Starter Plan</option>
                      <option value="AdvancedPlan">Advanced Plan</option>
                      <option value="ProPlan">Pro Plan</option>
                    </select>
                  </Field>

                  <Field label="Number of Months">
                    <input type="number" name="months" value={form.months} onChange={handleChange} className={inputClass} min="1" />
                  </Field>

                  <Field label="Total Price">
                    <input type="text" name="totalPrice" value={formatMoney(form.totalPrice)} readOnly className={`${inputClass} bg-slate-50`} />
                  </Field>

                  <Field label="Plan ID">
                    <input type="text" name="planID" value={form.planID} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="Promotion">
                    <input type="text" name="promotion" value={form.promotion} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="Additional Details" wide>
                    <textarea name="additionalDetails" value={form.additionalDetails} onChange={handleChange} className={textAreaClass} />
                  </Field>

                  <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={form.isActive}
                      onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                      className="h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">Product active</span>
                  </label>
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    type="submit"
                    className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
                  >
                    {editId ? "Update Product" : "Create Product"}
                  </button>
                </div>
              </form>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">Product List</h2>
                  <p className="text-sm text-slate-500">Browse and manage subscription products.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600">
                    {inactiveCount} inactive
                  </span>
                  <label className="relative w-full sm:w-80">
                    <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search products"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                    />
                  </label>
                </div>
              </div>

              {loading ? (
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  Loading products...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1040px] text-left text-sm">
                    <thead>
                      <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Price</th>
                        <th className="px-4 py-3 font-semibold">Category</th>
                        <th className="px-4 py-3 font-semibold">Stock</th>
                        <th className="px-4 py-3 font-semibold">Plan</th>
                        <th className="px-4 py-3 font-semibold">Months</th>
                        <th className="px-4 py-3 font-semibold">Total</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredProducts.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-500">
                            No products found.
                          </td>
                        </tr>
                      ) : (
                        filteredProducts.map((product) => (
                          <tr key={product._id} className="text-slate-700 transition hover:bg-slate-50">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                                  <AiFillShopping className="text-xl" />
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-950">{product.name}</div>
                                  {product.promotion && <p className="mt-1 text-xs text-slate-500">{product.promotion}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">{formatMoney(product.price)}</td>
                            <td className="px-4 py-4">{product.category}</td>
                            <td className="px-4 py-4">{product.stock}</td>
                            <td className="px-4 py-4">
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                                {product.subscriptionPlan || "None"}
                              </span>
                            </td>
                            <td className="px-4 py-4">{product.months || 1}</td>
                            <td className="px-4 py-4 font-semibold text-slate-950">{formatMoney(product.totalPrice)}</td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass(product.isActive)}`}>
                                {product.isActive ? <AiOutlineCheckCircle /> : <AiOutlineStop />}
                                {product.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEdit(product)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                                  aria-label="Edit product"
                                >
                                  <PencilSquareIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(product._id)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                                  aria-label="Delete product"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductAdmin;
